import {
  Button,
  Input,
  Segmented,
  Select,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useEffect, useState } from "react";

import { getAuditEvents } from "../api/internal_tool_endpoints";
import { eventColor } from "../utils/auditEventColor";

const DIMENSIONS = [
  { label: "Event type", value: "event_type" },
  { label: "Subject email", value: "subject_email" },
  { label: "Subject UID", value: "subject_uid" },
];

// Convenience options for the event-type filter. The API accepts any string;
// this is a curated shortlist of the current audit taxonomy, not a contract.
const EVENT_TYPES = [
  "invitation.sent",
  "invitation.activated",
  "invitation.failed",
  "provisioning.domain_blocked",
  "provisioning.domain_overridden",
  "user.created_via_admin",
  "user.account_type_set",
  "user.scope_updated",
  "user.workspace_removed",
  "user.updated_by_admin",
  "user.password_reset_by_admin",
  "user.deleted",
  "user.hard_deleted",
  "user.reactivated",
  "user.dashmote_admin_granted",
  "user.dashmote_admin_revoked",
  "impersonation.session_start",
  "impersonation.session_stop",
  "workspace.allowed_domains_updated",
  "workspace.combo_names_updated",
  "workspace.key_account_skus_updated",
];

const fmtTime = (ts) =>
  typeof ts === "string" ? ts.replace("T", " ").slice(0, 19) : "—";

const AuditTrailPage = () => {
  const [dimension, setDimension] = useState("event_type");
  const [value, setValue] = useState("");
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const fetchPage = async (reset) => {
    setLoading(true);
    try {
      const filter = value ? { [dimension]: value } : {};
      const { events: page, nextCursor } = await getAuditEvents({
        ...filter,
        cursor: reset ? undefined : cursor,
      });
      setEvents(reset ? page : (prev) => [...prev, ...page]);
      setCursor(nextCursor);
      setSearched(true);
    } catch (e) {
      message.error(e.message || "Failed to load audit events.");
    } finally {
      setLoading(false);
    }
  };

  // Load the newest events on mount so the page isn't empty.
  useEffect(() => {
    fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = () => {
    setCursor(null);
    fetchPage(true);
  };

  const onClear = () => {
    setDimension("event_type");
    setValue("");
  };

  const columns = [
    {
      title: "Time (UTC)",
      dataIndex: "timestamp_utc",
      key: "time",
      width: 170,
      render: (ts) => (
        <span className="text-[0.75rem] text-zinc-500">{fmtTime(ts)}</span>
      ),
    },
    {
      title: "Event",
      dataIndex: "event_type",
      key: "event",
      render: (type) => (
        <Tag color={eventColor(type)} className="text-[0.72rem]">
          {type}
        </Tag>
      ),
    },
    {
      title: "Actor",
      key: "actor",
      render: (_, r) => {
        const a = r.actor || {};
        return (
          <span className="text-[0.75rem]">
            {a.email || "—"}
            {a.type ? (
              // A cross-tenant `/admin` actor is the security-relevant one, so
              // it gets a colored tag that reads at a glance; the other tiers
              // stay a subtle label to keep the column quiet.
              a.is_dashmote_admin ? (
                <Tag color="volcano" className="ml-1.5 text-[0.6rem]">
                  {a.type}
                </Tag>
              ) : (
                <span className="ml-1.5 text-[0.65rem] text-zinc-400">
                  {a.type}
                </span>
              )
            ) : null}
          </span>
        );
      },
    },
    {
      title: "Subject",
      key: "subject",
      render: (_, r) => (
        <span className="text-[0.75rem]">
          {r.subject?.email || r.subject?.uid || (
            <span className="text-zinc-400">—</span>
          )}
        </span>
      ),
    },
  ];

  const renderDetail = (r) => {
    const extra = r.extra || {};
    const a = r.actor || {};
    // Older events (and the anonymous `self` flow) carry no identity axes, so
    // an axis is only shown when present — a boolean for the admin capability,
    // a non-empty list for the workspace-role sets.
    const knowsAdmin = typeof a.is_dashmote_admin === "boolean";
    const hasDiff =
      Object.prototype.hasOwnProperty.call(extra, "before") &&
      Object.prototype.hasOwnProperty.call(extra, "after");
    return (
      <div className="px-2 py-1 text-[0.75rem]">
        <div className="grid grid-cols-[130px_1fr] gap-y-1 gap-x-3 mb-2">
          <span className="text-zinc-400">Actor</span>
          <span className="font-mono">
            {a.email || a.uid || "—"}
            {a.type ? ` · ${a.type}` : ""}
          </span>
          <span className="text-zinc-400">Cross-tenant admin</span>
          <span className="font-mono">
            {knowsAdmin ? (a.is_dashmote_admin ? "Yes" : "No") : "—"}
          </span>
          <span className="text-zinc-400">Affiliation</span>
          <span className="font-mono">{a.affiliation || "—"}</span>
          <span className="text-zinc-400">Admin workspaces</span>
          <span className="font-mono">
            {a.admin_workspaces?.length ? a.admin_workspaces.join(", ") : "—"}
          </span>
          <span className="text-zinc-400">Super-admin workspaces</span>
          <span className="font-mono">
            {a.super_admin_workspaces?.length
              ? a.super_admin_workspaces.join(", ")
              : "—"}
          </span>
          <span className="text-zinc-400">Endpoint</span>
          <span className="font-mono">{r.request?.endpoint || "—"}</span>
          <span className="text-zinc-400">IP</span>
          <span className="font-mono">{r.request?.ip || "—"}</span>
          <span className="text-zinc-400">Event ID</span>
          <span className="font-mono">{r.event_id || "—"}</span>
        </div>
        {hasDiff ? (
          <div className="flex gap-3 flex-wrap">
            <div className="flex-1 min-w-[160px] border border-zinc-200 rounded-lg overflow-hidden">
              <div className="px-2 py-1 bg-zinc-50 text-zinc-400 text-[0.65rem] uppercase tracking-wide">
                Before
              </div>
              <pre className="px-2 py-1 m-0 font-mono text-[0.7rem] whitespace-pre-wrap">
                {JSON.stringify(extra.before, null, 2)}
              </pre>
            </div>
            <div className="flex-1 min-w-[160px] border border-zinc-200 rounded-lg overflow-hidden">
              <div className="px-2 py-1 bg-zinc-50 text-zinc-400 text-[0.65rem] uppercase tracking-wide">
                After
              </div>
              <pre className="px-2 py-1 m-0 font-mono text-[0.7rem] whitespace-pre-wrap">
                {JSON.stringify(extra.after, null, 2)}
              </pre>
            </div>
          </div>
        ) : (
          <pre className="m-0 font-mono text-[0.7rem] whitespace-pre-wrap text-zinc-600">
            {JSON.stringify(extra, null, 2)}
          </pre>
        )}
      </div>
    );
  };

  return (
    <div className="ml-[240px] w-[calc(100%-240px)] p-6">
      <Typography.Title level={3}>Audit Trail</Typography.Title>
      <p className="text-zinc-500 text-[0.8rem] mb-4">
        Every recorded change — user access, impersonation, invite policy. Filter
        by one dimension at a time.
      </p>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <Segmented
          options={DIMENSIONS}
          value={dimension}
          onChange={(d) => {
            setDimension(d);
            setValue("");
          }}
        />
        {dimension === "event_type" ? (
          <Select
            showSearch
            allowClear
            placeholder="Any event type"
            value={value || undefined}
            onChange={(v) => setValue(v || "")}
            options={EVENT_TYPES.map((t) => ({ value: t, label: t }))}
            style={{ minWidth: 260 }}
            aria-label="Event type"
          />
        ) : (
          <Input
            placeholder={
              dimension === "subject_email"
                ? "user@example.com"
                : "Firebase uid"
            }
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onPressEnter={onSearch}
            style={{ minWidth: 260 }}
            aria-label={dimension}
          />
        )}
        <Button type="primary" onClick={onSearch} loading={loading}>
          Search
        </Button>
        <Button onClick={onClear}>Clear</Button>
        <span className="text-zinc-400 text-[0.72rem] w-full">
          One filter at a time — the API accepts a single dimension.
        </span>
      </div>

      <Table
        columns={columns}
        dataSource={events}
        rowKey={(r, i) => r.event_id || `${r.timestamp_utc}-${i}`}
        pagination={false}
        loading={loading && events.length === 0}
        size="small"
        expandable={{ expandedRowRender: renderDetail }}
        locale={{
          emptyText: searched
            ? "No audit events for this filter."
            : "Loading…",
        }}
      />

      {cursor ? (
        <div className="mt-3">
          <Button onClick={() => fetchPage(false)} loading={loading}>
            Load more
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default AuditTrailPage;
